'use client';

import { createContext, useContext } from 'react';

/**
 * Floating 컴포넌트(STooltip/SPopover/SSelect listbox/SDropdownButton/SDatePicker 등)가
 * 자신의 Portal 을 어디로 렌더할지 결정하는 컨텍스트.
 *
 * 기본값(null)이면 각 컴포넌트는 body 로 portal 한다.
 * SModalContainer 는 모달이 열려있는 동안 이 값을 해당 모달 Content 자손의 전용 노드로
 * 채워서, 모달 안에서 열린 floating 이 같은 모달의 딤 뒤에 가려지지 않고 위로 뜨게 한다.
 * 반대로 모달 밖(베이스 화면)에서 열린 floating 은 여전히 body(z-50)로 나가므로,
 * 나중에 뜬 모달이 정상적으로 그 위를 덮는다. (소속에 따라 상대적으로 결정)
 */
const PortalContainerContext = createContext<HTMLElement | null>(null);
const InsideModalContext = createContext(false);

export const PortalContainerProvider = PortalContainerContext.Provider;
export const InsideModalProvider = InsideModalContext.Provider;

/**
 * 현재 소속된 Portal 컨테이너를 반환한다.
 * Radix `*.Portal` 의 `container`, Ariakit 의 `portalElement` 에 그대로 넘기면 되며,
 * 컨테이너가 없을 때(모달 밖)는 `undefined` 를 반환해 각 라이브러리 기본값(body)을 쓴다.
 */
export function usePortalContainer(): HTMLElement | undefined {
  return useContext(PortalContainerContext) ?? undefined;
}

/**
 * 현재 floating 이 모달 내부 컨텍스트에서 렌더링되는지 반환한다.
 * portal target ref 가 아직 준비되지 않은 첫 렌더에서도 true를 유지해
 * 모달 내부 전용 우회 로직이 깜빡이지 않도록 한다.
 */
export function useInsideModal(): boolean {
  return useContext(InsideModalContext);
}
